import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import FormHeader from "@/components/FormHeader"
import Modal from "@/components/Modal"
import api from "@/lib/axios"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import toast from "react-hot-toast"
import { FaFilePdf } from "react-icons/fa6"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pagination } from "./Pagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { getListPeriode } from "@/services/getData"
import { Label } from "@/components/ui/label"
import { Periode } from "@/types"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
}

const postDataGenerate = async ({
  id_sys_periode,
  jumlah_berkas,
}: {
  id_sys_periode: string
  jumlah_berkas: string | number
}) => {
  const response = await api.post("/penerimaan/sertifikat-cbt/genrate-file", {
    id_sys_periode,
    jumlah_berkas,
  })
  return response.data
}

export function DataTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [showGenerate, setShowGenerate] = useState(false)
  const [form, setForm] = useState({
    id_sys_periode: "",
    jumlah_berkas: "",
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const { data: dataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  })
  const listPeriodeCbt =
    dataPeriode && dataPeriode?.length > 0
      ? dataPeriode?.filter((periode: Periode) => {
          return periode.jalur_jenis?.nama_jalur_jenis_slug == "cbt"
        })
      : []

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postDataGenerate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSertifikatTteSumber"] })
      setShowGenerate(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleGenerateSertifikat = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutate({ ...form })
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <Input
          placeholder="Filter by keterangan..."
          value={table.getColumn("keterangan")?.getFilterValue() as string}
          onChange={(event) =>
            table.getColumn("keterangan")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <div className="flex gap-x-3">
          <Button
            className="flex items-center gap-x-3"
            onClick={() => setShowGenerate(true)}
          >
            Generate
            <FaFilePdf />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} />
      </div>

      <Modal
        show={showGenerate}
        onClose={() => setShowGenerate(false)}
        closeable={!isPending}
      >
        <form onSubmit={handleGenerateSertifikat}>
          <FormHeader
            icon={<FaFilePdf />}
            title="Generate Sertifikat CBT"
            description={`Apakah anda yakin akan melakukan generate sertifikat?`}
          />
          <div>
            <Label>Periode</Label>
            <Select
              required
              value={form.id_sys_periode}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Periode"} />
              </SelectTrigger>
              <SelectContent>
                {listPeriodeCbt?.map((periode: Periode) => (
                  <SelectItem
                    key={periode.id_sys_periode}
                    value={`${periode.id_sys_periode}`}
                  >
                    {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                    Gelombang {periode.gelombang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Jumlah Berkas</Label>
            <Input
              type="number"
              placeholder="Ketik jumlah berkas..."
              value={form.jumlah_berkas}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  jumlah_berkas: event.target.value,
                }))
              }
            />
          </div>
          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPending}
              onClick={() => setShowGenerate(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Ya, Generate"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
